(() => {
  if (window.saintStorefront) return;
  window.saintStorefront = true;
  let busy = false;
  let previousFocus;
  const disabled = new Map();
  const drawer = () => document.getElementById('CartDrawer');
  const root = () => drawer()?.dataset.root || '/';
  const status = message => { const node = drawer()?.querySelector('[data-cart-status]'); if (node) node.textContent = message; };

  function lock(value) {
    busy = value;
    drawer()?.setAttribute('aria-busy', String(value));
    if (value) {
      document.querySelectorAll('[data-add-button], .purchase-form input, .purchase-form select, #CartDrawerBody button, #CartDrawerBody input').forEach(node => {
        disabled.set(node, node.disabled);
        node.disabled = true;
      });
    } else {
      disabled.forEach((value, node) => { if (node.isConnected) node.disabled = value; });
      disabled.clear();
    }
  }

  function show(dialog, trigger) {
    if (!dialog || dialog.open) return;
    previousFocus = trigger || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('dialog-open');
  }

  function bindDialog(dialog) {
    if (!dialog || dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('dialog-open');
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
  }

  function render(html, focus) {
    if (!html) throw new Error('Missing cart section');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.getElementById('CartDrawerBody');
    const current = document.getElementById('CartDrawerBody');
    if (!incoming || !current) throw new Error('Missing cart body');
    current.replaceWith(incoming);
    const count = Number(incoming.dataset.count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count ? ` (${count})` : ''; });
    if (focus && drawer().open) {
      const line = [...incoming.querySelectorAll('[data-cart-line]')].find(node => node.dataset.cartLine === focus.key);
      const target = line?.querySelector(focus.selector) || drawer().querySelector('[data-dialog-close]');
      target?.focus({ preventScroll: true });
    }
  }

  async function refresh(focus) {
    const response = await fetch(`${root()}?section_id=cart-drawer`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Cart unavailable');
    render(await response.text(), focus);
  }

  async function openCart(trigger) {
    if (busy) return;
    show(drawer(), trigger);
    status(drawer().dataset.loading);
    lock(true);
    try { await refresh(); status(''); }
    catch { status(drawer().dataset.error); }
    finally { lock(false); }
  }

  async function mutate(endpoint, body, focus) {
    const response = await fetch(`${root()}cart/${endpoint}.js`, {
      method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
      ...(body instanceof FormData ? { body } : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(typeof result.description === 'string' ? result.description : drawer().dataset.error);
      error.confirmed = true;
      throw error;
    }
    try { render(result.sections?.['cart-drawer'], focus); }
    catch { await refresh(focus); }
  }

  async function add(form, trigger) {
    if (busy) return;
    const data = new FormData(form);
    data.set('sections', 'cart-drawer');
    data.set('sections_url', window.location.pathname);
    const message = form.querySelector('.purchase-status');
    const fallback = form.querySelector('.purchase-cart-link');
    const buttons = [...document.querySelectorAll('[data-add-button]')];
    const labels = buttons.map(button => button.textContent);
    message.textContent = '';
    fallback.hidden = true;
    lock(true);
    buttons.forEach(button => { button.textContent = button.dataset.adding; });
    try {
      await mutate('add', data);
      message.textContent = drawer().dataset.added;
      status(drawer().dataset.added);
      show(drawer(), trigger);
    } catch (error) {
      message.textContent = error.confirmed ? error.message : drawer().dataset.error;
      fallback.hidden = false;
    } finally {
      lock(false);
      buttons.forEach((button, index) => { button.textContent = labels[index]; });
    }
  }

  async function change(line, quantity, selector) {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    const key = line.dataset.cartLine;
    lock(true);
    status(drawer().dataset.loading);
    try {
      await mutate('change', { id: key, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }, { key, selector });
      status(drawer().dataset.updated);
    } catch (error) {
      if (error.confirmed) {
        try { await refresh({ key, selector }); } catch {}
      }
      status(error.confirmed ? error.message : drawer().dataset.error);
    } finally { lock(false); }
  }

  document.addEventListener('submit', event => {
    if (!drawer() || typeof drawer().showModal !== 'function') return;
    if (event.target.matches('.purchase-form')) {
      event.preventDefault();
      add(event.target, event.submitter);
    } else if (event.target.matches('.drawer-summary') && busy) event.preventDefault();
  });

  document.addEventListener('click', event => {
    const target = event.target.closest('a, button');
    if (!target) return;
    if (target.matches('[data-dialog-close]')) { target.closest('dialog')?.close(); return; }
    if (target.matches('[data-cart-open]') && drawer() && typeof drawer().showModal === 'function' && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault(); openCart(target); return;
    }
    if (target.matches('[data-image-zoom]') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      const dialog = document.getElementById('ProductZoom');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      dialog.querySelector('img').src = target.href;
      show(dialog, target); return;
    }
    const line = target.closest('[data-cart-line]');
    if (!line) return;
    if (target.matches('[data-cart-remove]')) change(line, 0, '[data-cart-remove]');
    if (target.matches('[data-quantity-step]')) {
      const input = line.querySelector('[data-cart-quantity]');
      const value = Number(input.value) + Number(target.dataset.quantityStep);
      change(line, Math.max(0, value), `[data-quantity-step="${target.dataset.quantityStep}"]`);
    }
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-cart-quantity]')) {
      if (!event.target.reportValidity() || event.target.value === '') return;
      change(event.target.closest('[data-cart-line]'), Number(event.target.value), '[data-cart-quantity]');
    }
  });

  let tick = false;
  function updateMobilePurchase() {
    const bar = document.querySelector('[data-mobile-purchase]');
    const form = document.querySelector('.purchase-form');
    if (bar && form) bar.hidden = form.getBoundingClientRect().bottom > 0;
    tick = false;
  }
  window.addEventListener('scroll', () => {
    if (!tick) { tick = true; requestAnimationFrame(updateMobilePurchase); }
  }, { passive: true });
  window.addEventListener('resize', updateMobilePurchase);
  function initialize() { document.querySelectorAll('dialog').forEach(bindDialog); updateMobilePurchase(); }
  document.addEventListener('shopify:section:load', initialize);
  initialize();
})();
